import merge from 'lodash/merge';
import { Dispatch } from 'redux';

import API from '~/Api';
import { USER_ID_KEY } from '~/constants';
import i18n from '~/i18n/config';
import analytics from '~/services/analytics';
import errorLogger from '~/services/ErrorLogger';
import localStorage from '~/services/storage/localStorage';
import { RootState } from '~/store/reducers';
import { ProfileAction, ProfileActionTypes } from '~/store/types/profile';
import { ExtraFields, ProfileType } from '~/types/profile';
import { SUBSCRIPTION_STATUS } from '~/types/subscription';

let updateExtraFieldsPromise: Promise<void> | null = null;

const loadAndDispatchActualCustomerData = async (
  dispatch: Dispatch<ProfileAction>,
  getState: () => RootState,
  from?: 'signUp',
): Promise<void> => {
  const profile = await API.getProfile();
  const currentProfile = getState().profile;

  analytics.checkAndUpdateUserId(profile.id);
  errorLogger.setUser(profile.id);

  localStorage.setByKey(USER_ID_KEY, profile.id);

  if (profile.platform_lang !== i18n.instance.language) {
    i18n.instance.changeLanguage(profile.platform_lang);
  }

  analytics.addDefaultEventParams({
    email: profile.email,
    ...profile.backend_ab_tests,
    ...(process.env.GITHUB_SHA ? { github_sha: process.env.GITHUB_SHA } : {}),
  });

  analytics.setUserProperties({
    email: profile.email,
  });

  dispatch({
    type: ProfileActionTypes.UPDATE_PROFILE,
    payload: {
      ...currentProfile,
      ...profile,
      extra_fields: merge(
        currentProfile?.extra_fields || {},
        profile.extra_fields || {},
      ),
    },
  });

  const extra_fields: Record<string, any> = {};

  if (from === 'signUp') {
    extra_fields.sign_up_at = new Date().toISOString();
  }

  if (Object.keys(extra_fields).length) {
    await updateUserExtraFields(extra_fields)(dispatch, getState);
  }
};

export const loadAndDispatchActualSubscriptionData = () => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      const profile = getState().profile;
      const user_subscription = await API.getSubscription();

      dispatch({
        type: ProfileActionTypes.UPDATE_PROFILE,
        payload: {
          ...profile,
          user_subscription,
        },
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const loadAndDispatchActualPolicyData = () => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      const profile = getState().profile;
      const user_policy = await API.getPolicy();

      dispatch({
        type: ProfileActionTypes.UPDATE_PROFILE,
        payload: {
          ...profile,
          user_policy,
        },
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const updateUserFields = (newFields: Partial<ProfileType>) => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      const profile = getState().profile;

      dispatch({
        type: ProfileActionTypes.UPDATE_PROFILE,
        payload: {
          ...profile,
          ...newFields,
        },
      });

      await API.updateProfile({ ...newFields });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const updateUserExtraFields = (newExtraFields: Partial<ExtraFields>) => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      if (updateExtraFieldsPromise) {
        await updateExtraFieldsPromise;
      }
      updateExtraFieldsPromise = (async (): Promise<void> => {
        const profile = getState().profile;
        const mergedExtraFields = merge(
          {},
          profile.extra_fields,
          newExtraFields,
        );
        dispatch({
          type: ProfileActionTypes.UPDATE_PROFILE,
          payload: {
            ...profile,
            extra_fields: mergedExtraFields,
          },
        });
        await API.updateProfile({ extra_fields: mergedExtraFields });
      })();
      await updateExtraFieldsPromise;
    } catch (e: any) {
      console.error(e);
      throw e;
    } finally {
      updateExtraFieldsPromise = null;
    }
  };
};

export const checkIsUserLoggedIn = () => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      await loadAndDispatchActualCustomerData(dispatch, getState);

      dispatch({
        type: ProfileActionTypes.SIGN_IN,
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const signIn = (
  email: string,
  password: string,
  afterSignInCallback?: () => Promise<void>,
) => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      await API.signIn(email, password);
      await afterSignInCallback?.();
      await loadAndDispatchActualCustomerData(dispatch, getState);

      dispatch({
        type: ProfileActionTypes.SIGN_IN,
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const signUp = (data: {
  email: string;
  name: string;
  password: string;
  extraFields?: Partial<ExtraFields>;
}) => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      await API.signUp({
        email: data.email,
        name: data.name,
        password: data.password,
        extra_fields: data.extraFields,
      });

      if (data.extraFields) {
        dispatch({
          type: ProfileActionTypes.UPDATE_PROFILE,
          payload: {
            ...getState().profile,
            extra_fields: merge(
              getState().profile.extra_fields,
              data.extraFields,
            ),
          },
        });
      }

      await loadAndDispatchActualCustomerData(dispatch, getState, 'signUp');

      dispatch({
        type: ProfileActionTypes.SIGN_IN,
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const signOut = () => {
  return async (dispatch: Dispatch<ProfileAction>): Promise<void> => {
    analytics.trackEvent('general - log out');
    API.signOut();
    dispatch({
      type: ProfileActionTypes.SIGN_OUT,
    });
  };
};

export const loadAndSetActualProfileData = () => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      await loadAndDispatchActualCustomerData(dispatch, getState);

      dispatch({
        type: ProfileActionTypes.SIGN_IN,
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};

export const updateProfileSubscriptionStatus = (
  newStatus: SUBSCRIPTION_STATUS,
) => {
  return async (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): Promise<void> => {
    const profile = getState().profile;

    dispatch({
      type: ProfileActionTypes.UPDATE_PROFILE,
      payload: {
        ...profile,
        user_subscription: {
          ...profile.user_subscription,
          status: newStatus,
        },
      },
    });
  };
};

export const increaseResponseCountTotal = () => {
  return async (dispatch: Dispatch<ProfileAction>): Promise<void> => {
    dispatch({
      type: ProfileActionTypes.INCREASE_RESPONSE_COUNT,
    });
  };
};

export const setUser = (userData: Partial<ProfileType>) => {
  return (
    dispatch: Dispatch<ProfileAction>,
    getState: () => RootState,
  ): void => {
    try {
      const profile = getState().profile;

      dispatch({
        type: ProfileActionTypes.UPDATE_PROFILE,
        payload: {
          ...profile,
          ...userData,
        },
      });
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
};
